/**
 * SEO Utilities
 *
 * Provides functions for managing meta tags, structured data,
 * and multilingual SEO (hreflang tags).
 */

import { useEffect } from 'react';
import i18n from '../i18n';

// Site configuration
export const SITE_CONFIG = {
  name: 'xtFORM',
  domain: 'https://xtform.com',
  defaultImage: 'https://xtform.com/og-image.png',
  twitterHandle: '@xtform',
};

// Supported languages for hreflang
export const HREFLANG_LANGUAGES = [
  'en', 'he', 'ar', 'es', 'fr', 'de', 'pt', 'ru',
  'ja', 'ko', 'zh', 'hi', 'bn', 'vi', 'tr', 'it',
  'th', 'pl', 'nl', 'id'
];

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  canonical?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  locale?: string;
}

/**
 * Update document meta tags
 */
export function updateMetaTags(props: SEOProps): void {
  const {
    title,
    description,
    keywords,
    image = SITE_CONFIG.defaultImage,
    url = window.location.href,
    type = 'website',
    noindex = false,
    canonical,
    publishedTime,
    modifiedTime,
    author,
    locale = i18n.language,
  } = props;

  // Update title
  if (title) {
    document.title = `${title} | ${SITE_CONFIG.name}`;
    updateMeta('og:title', title);
    updateMeta('twitter:title', title);
  }

  // Update description
  if (description) {
    updateMeta('description', description);
    updateMeta('og:description', description);
    updateMeta('twitter:description', description);
  }

  // Update keywords
  if (keywords) {
    updateMeta('keywords', keywords);
  }

  // Update image
  if (image) {
    updateMeta('og:image', image);
    updateMeta('twitter:image', image);
  }

  // Update URL
  updateMeta('og:url', url);
  updateMeta('twitter:url', url);

  // Update type
  updateMeta('og:type', type);

  // Update locale
  updateMeta('og:locale', localeToOG(locale));

  // Canonical URL
  if (canonical) {
    updateLink('canonical', canonical);
  }

  // Robots
  if (noindex) {
    updateMeta('robots', 'noindex, nofollow');
  }

  // Article-specific meta
  if (type === 'article') {
    if (publishedTime) {
      updateMeta('article:published_time', publishedTime);
    }
    if (modifiedTime) {
      updateMeta('article:modified_time', modifiedTime);
    }
    if (author) {
      updateMeta('article:author', author);
    }
  }
}

/**
 * Update or create a meta tag
 */
function updateMeta(name: string, content: string): void {
  // Try property first (for OG tags), then name
  let meta = document.querySelector(`meta[property="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  }

  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('article:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
}

/**
 * Update or create a link tag
 */
function updateLink(rel: string, href: string): void {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;

  if (link) {
    link.setAttribute('href', href);
  } else {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);
    document.head.appendChild(link);
  }
}

/**
 * Convert language code to OG locale format
 */
function localeToOG(lang: string): string {
  const localeMap: Record<string, string> = {
    en: 'en_US',
    he: 'he_IL',
    ar: 'ar_SA',
    es: 'es_ES',
    fr: 'fr_FR',
    de: 'de_DE',
    pt: 'pt_BR',
    ru: 'ru_RU',
    ja: 'ja_JP',
    ko: 'ko_KR',
    zh: 'zh_CN',
    hi: 'hi_IN',
  };
  return localeMap[lang] || 'en_US';
}

/**
 * Add hreflang tags for multilingual SEO
 */
export function updateHreflangTags(basePath: string = '/'): void {
  // Remove existing hreflang tags
  document.querySelectorAll('link[hreflang]').forEach(el => el.remove());

  // Add hreflang for each supported language
  HREFLANG_LANGUAGES.forEach(lang => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    link.setAttribute('href', `${SITE_CONFIG.domain}${basePath}${basePath.includes('?') ? '&' : '?'}lang=${lang}`);
    document.head.appendChild(link);
  });

  // Add x-default
  const defaultLink = document.createElement('link');
  defaultLink.setAttribute('rel', 'alternate');
  defaultLink.setAttribute('hreflang', 'x-default');
  defaultLink.setAttribute('href', `${SITE_CONFIG.domain}${basePath}`);
  document.head.appendChild(defaultLink);
}

/**
 * Generate JSON-LD structured data for a form
 */
export function generateFormStructuredData(form: {
  name: string;
  description?: string;
  type: 'form' | 'poll' | 'quiz' | 'live_quiz' | 'product' | 'order';
  url: string;
  dateCreated?: string;
  dateModified?: string;
}): string {
  const typeMap: Record<string, string> = {
    form: 'WebApplication',
    poll: 'WebApplication',
    quiz: 'Quiz',
    live_quiz: 'Quiz',
    product: 'Product',
    order: 'WebApplication',
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': typeMap[form.type] || 'WebApplication',
    name: form.name,
    description: form.description || `${form.name} - Created with ${SITE_CONFIG.name}`,
    url: form.url,
    applicationCategory: form.type === 'quiz' || form.type === 'live_quiz' ? 'EducationalApplication' : 'BusinessApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    ...(form.dateCreated && { dateCreated: form.dateCreated }),
    ...(form.dateModified && { dateModified: form.dateModified }),
    provider: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.domain,
    },
  };

  return JSON.stringify(structuredData);
}

/**
 * Add structured data script to document
 */
export function addStructuredData(data: string, id: string = 'dynamic-structured-data'): void {
  // Remove existing script with same ID
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = data;
  document.head.appendChild(script);
}

/**
 * React hook for SEO management
 */
export function useSEO(props: SEOProps): void {
  useEffect(() => {
    updateMetaTags(props);

    // Cleanup function to reset to defaults when component unmounts
    return () => {
      // Optional: Reset to default meta tags
    };
  }, [props.title, props.description, props.image, props.url]);
}

/**
 * React hook for hreflang tags
 */
export function useHreflang(basePath: string = '/'): void {
  useEffect(() => {
    updateHreflangTags(basePath);

    return () => {
      // Remove hreflang tags when component unmounts
      document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    };
  }, [basePath]);
}

/**
 * React hook for structured data
 * Accepts either a JSON string or an object (which will be stringified)
 */
export function useStructuredData(data: Record<string, unknown> | string | null, id?: string): void {
  useEffect(() => {
    if (data) {
      const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
      addStructuredData(jsonString, id);
    }

    return () => {
      if (id) {
        const existing = document.getElementById(id);
        if (existing) {
          existing.remove();
        }
      }
    };
  }, [data, id]);
}
